import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { FullPageLoader } from '@/components/ui/full-page-loader';

interface TeacherRouteProps {
  children: ReactNode;
}

/**
 * RFC-146 Phase 0 — Teacher route guard.
 *
 * Order of checks (per RFC §3.6):
 * 1. Auth (redirect to /auth if signed out)
 * 2. Teacher status: profiles.is_teacher === true OR tier === 'admin'
 * 3. Children render (children themselves render any platform notice for native vs. web)
 *
 * Platform-specific notices belong in the route content, not in this guard, so a
 * teacher who lands on /teach via a deep link sees the same gate decision regardless
 * of device, then gets the appropriate "manage on web" message on native.
 */
export function TeacherRoute({ children }: TeacherRouteProps) {
  const { user, tier, loading: authLoading, tierInitialized } = useAuth();
  const [isTeacher, setIsTeacher] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!user) {
        if (!cancelled) {
          setIsTeacher(false);
          setChecking(false);
        }
        return;
      }
      // Admins always have access.
      if (tier === 'admin') {
        if (!cancelled) {
          setIsTeacher(true);
          setChecking(false);
        }
        return;
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('is_teacher')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error('[TeacherRoute] Failed to load profile:', error);
        setIsTeacher(false);
      } else {
        setIsTeacher(data?.is_teacher === true);
      }
      setChecking(false);
    }
    if (!authLoading && tierInitialized) {
      check();
    }
    return () => {
      cancelled = true;
    };
  }, [user, tier, authLoading, tierInitialized]);

  if (authLoading || !tierInitialized || checking) {
    return <FullPageLoader />;
  }
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  if (!isTeacher) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
